/**
 * Identity is bound to the browser: reloading, closing the tab or locking the
 * phone all return the player to their own identity. A different browser is a
 * different player — there is no cross-device identity by design, and no
 * recovery link: a room lives and dies with the browser that created it.
 *
 * Every access is guarded: localStorage throws outright in some contexts
 * (private windows, browsers set to block site data), and a thrown storage
 * error must never take the app down.
 */

const PLAYER_PREFIX = 'sh.player.';
const ADMIN_PREFIX = 'sh.admin.';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* A player who cannot persist simply re-joins as someone new. */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getPlayerToken(roomToken: string): string | null {
  return read(PLAYER_PREFIX + roomToken);
}

export function setPlayerToken(roomToken: string, token: string): void {
  write(PLAYER_PREFIX + roomToken, token);
}

export function clearPlayerToken(roomToken: string): void {
  remove(PLAYER_PREFIX + roomToken);
}

export function getAdminToken(roomToken: string): string | null {
  return read(ADMIN_PREFIX + roomToken);
}

export function setAdminToken(roomToken: string, token: string): void {
  write(ADMIN_PREFIX + roomToken, token);
}

/**
 * The two shareable links. Neither carries the admin token: admin rights live
 * only in the creating browser's storage, and there is deliberately no link
 * that confers them. If that storage is lost, the room is lost with it.
 */
export function joinLinkFor(roomToken: string, origin: string): string {
  return `${origin}/r/${roomToken}`;
}

export function projectorLinkFor(roomToken: string, origin: string): string {
  return `${origin}/r/${roomToken}/projector`;
}
